import * as Api from '@ViewModels';
import { IImpersonationContext } from '@ViewModels';
import { css } from 'aphrodite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Waypoint } from 'react-waypoint';
import { useDebounceCallback } from '../../../../../hooks/useDebounceValue';
import { useAccountLoad, useInfiniteFilterGoogleBusinessReviews, useInfiniteReviews } from '../../../../../queries';
import { Button } from '../../../../../web/components-v2/Button';
import {
	Dropdown,
	DropdownContent,
	DropdownRadioGroup,
	DropdownRadioItem,
	DropdownSelectTrigger,
} from '../../../../../web/components-v2/Dropdown';
import { InputGroupAddon, InputGroupInput, InputGroupV2 } from '../../../../../web/components-v2/InputGroup';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { SearchIcon2 } from '../../../../../web/components/svgs/icons/SearchIcon2';
import { ReviewCard } from './ReviewCard';
import { TestimonialModal } from './TestimonialModal';
import { styleSheet } from './styles';

interface ISortDescriptor {
	sort: 'asc' | 'desc';
	sortBy: 'ratingStringSort' | 'createTime';
}

const SORT_OPTIONS: { value: string; label: string; sortDescriptor: ISortDescriptor }[] = [
	{ value: 'hr-desc', label: 'Highest rated', sortDescriptor: { sort: 'desc', sortBy: 'ratingStringSort' } },
	{ value: 'lr-asc', label: 'Lowest rated', sortDescriptor: { sort: 'asc', sortBy: 'ratingStringSort' } },
	{ value: 'd-desc', label: 'Newest first', sortDescriptor: { sort: 'desc', sortBy: 'createTime' } },
	{ value: 'd-asc', label: 'Oldest first', sortDescriptor: { sort: 'asc', sortBy: 'createTime' } },
];

type Props = {
	impersonationContext: IImpersonationContext;
	accountId: string;
};

export const GoogleReviewsSidebarComponent = ({ impersonationContext, accountId }: Props) => {
	const [selectedReview, setSelectedReview] = useState<Api.IGoogleBusinessReview | null>(null);
	const [search, setSearch] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [hasResponded, setHasResponded] = useState<boolean | null>(null);
	const [sortDescriptor, setSortDescriptor] = useState<ISortDescriptor>({ sort: 'desc', sortBy: 'createTime' });
	const { data: account } = useAccountLoad({ accountId });

	const reviewsQuery = useInfiniteReviews({
		impersonationContext,
		enabled: Boolean(impersonationContext) && !debouncedSearch && hasResponded === null,
		refetchOnWindowFocus: false,
		pageSize: 10,
		sort: sortDescriptor.sort,
		sortBy: sortDescriptor.sortBy,
	});

	const filterRequest = useMemo(() => {
		if (!debouncedSearch && hasResponded === null) return undefined;
		return {
			Name: debouncedSearch || undefined,
			HasResponded: hasResponded,
			Sort: sortDescriptor.sort,
			SortBy: sortDescriptor.sortBy,
		};
	}, [debouncedSearch, hasResponded, sortDescriptor]);

	const filteredReviewsQuery = useInfiniteFilterGoogleBusinessReviews({
		impersonationContext,
		enabled: Boolean(impersonationContext) && Boolean(filterRequest),
		refetchOnWindowFocus: false,
		pageSize: 10,
		filterRequest,
		sort: sortDescriptor.sort,
		sortBy: sortDescriptor.sortBy,
	});

	const debouncedSetSearch = useDebounceCallback((value: string) => setDebouncedSearch(value), 500);

	useEffect(() => {
		debouncedSetSearch(search);
	}, [search, debouncedSetSearch]);

	const reviews = useMemo(() => {
		if (!debouncedSearch && hasResponded === null) {
			return reviewsQuery.data?.pages?.flatMap(page => page.values) ?? [];
		}
		return filteredReviewsQuery.data?.pages?.flatMap(page => page.values) ?? [];
	}, [debouncedSearch, hasResponded, reviewsQuery.data?.pages, filteredReviewsQuery.data?.pages]);

	const isPending = useMemo(() => {
		if (!debouncedSearch && hasResponded === null) return reviewsQuery.isPending;
		return filteredReviewsQuery.isPending;
	}, [debouncedSearch, hasResponded, reviewsQuery.isPending, filteredReviewsQuery.isPending]);

	const hasNextPage = useMemo(() => {
		if (!debouncedSearch && hasResponded === null) return reviewsQuery.hasNextPage;
		return filteredReviewsQuery.hasNextPage;
	}, [debouncedSearch, hasResponded, reviewsQuery.hasNextPage, filteredReviewsQuery.hasNextPage]);

	const isFetchingNextPage = useMemo(() => {
		if (!debouncedSearch && hasResponded === null) return reviewsQuery.isFetchingNextPage;
		return filteredReviewsQuery.isFetchingNextPage;
	}, [debouncedSearch, hasResponded, reviewsQuery.isFetchingNextPage, filteredReviewsQuery.isFetchingNextPage]);

	const fetchNextPage = useCallback(() => {
		if (!debouncedSearch && hasResponded === null) {
			reviewsQuery.fetchNextPage();
		} else {
			filteredReviewsQuery.fetchNextPage();
		}
	}, [debouncedSearch, hasResponded, reviewsQuery, filteredReviewsQuery]);

	const handleResponseFilterClick = useCallback((responded: boolean | null) => {
		setHasResponded(responded);
	}, []);

	const handleClearFilters = useCallback(() => {
		setHasResponded(null);
		setSearch('');
		setDebouncedSearch('');
	}, []);

	const getCurrentSortValue = () =>
		SORT_OPTIONS.find(
			opt => opt.sortDescriptor.sort === sortDescriptor.sort && opt.sortDescriptor.sortBy === sortDescriptor.sortBy
		)?.value ?? 'd-desc';

	const getCurrentSortLabel = () =>
		SORT_OPTIONS.find(
			opt => opt.sortDescriptor.sort === sortDescriptor.sort && opt.sortDescriptor.sortBy === sortDescriptor.sortBy
		)?.label ?? 'Newest first';

	const onSortValueChange = (value: string) => {
		const option = SORT_OPTIONS.find(opt => opt.value === value);
		if (option) setSortDescriptor(option.sortDescriptor);
	};

	const hasActiveFilters = debouncedSearch !== '' || hasResponded !== null;

	const handleReviewClick = useCallback((review: Api.IGoogleBusinessReview) => {
		setSelectedReview(review);
	}, []);

	const handleCloseModal = useCallback(() => {
		setSelectedReview(null);
	}, []);

	if (!impersonationContext) {
		return null;
	}

	const header = (
		<header className={css(styleSheet.header)}>
			<h2 className={css(styleSheet.headerTitle)}>Google Reviews</h2>
			<p className={css(styleSheet.headerDescription)}>
				Recent reviews from {account?.companyName} Google Business Profile
			</p>
		</header>
	);

	const filtersSection = (
		<div className={css(styleSheet.filtersSection)}>
			<InputGroupV2 styles={[styleSheet.searchInputWrapper]}>
				<InputGroupAddon align='inlineStart'>
					<SearchIcon2 className={css(styleSheet.searchIcon)} />
				</InputGroupAddon>
				<InputGroupInput
					id='google-reviews-sidebar-search'
					placeholder='Search by author name'
					styles={[styleSheet.searchInput]}
					value={search}
					onChange={e => setSearch(e.target.value)}
				/>
			</InputGroupV2>
			<div className={css(styleSheet.filterPills)}>
				<Button
					variant={hasResponded === null ? 'primary' : 'secondary'}
					size='sm'
					onClick={() => handleResponseFilterClick(null)}
				>
					All
				</Button>
				<Button
					variant={hasResponded === true ? 'primary' : 'secondary'}
					size='sm'
					onClick={() => handleResponseFilterClick(true)}
				>
					Replied
				</Button>
				<Button
					variant={hasResponded === false ? 'primary' : 'secondary'}
					size='sm'
					onClick={() => handleResponseFilterClick(false)}
				>
					Not Replied
				</Button>
			</div>
			<div className={css(styleSheet.sortRow)}>
				<Dropdown>
					<DropdownSelectTrigger styles={[styleSheet.sortSelect]}>{getCurrentSortLabel()}</DropdownSelectTrigger>
					<DropdownContent align='start'>
						<DropdownRadioGroup value={getCurrentSortValue()} onValueChange={onSortValueChange}>
							{SORT_OPTIONS.map(option => (
								<DropdownRadioItem key={option.value} value={option.value}>
									{option.label}
								</DropdownRadioItem>
							))}
						</DropdownRadioGroup>
					</DropdownContent>
				</Dropdown>
				{hasActiveFilters && (
					<Button variant='link' size='sm' onClick={handleClearFilters}>
						Clear filters
					</Button>
				)}
			</div>
		</div>
	);

	if (isPending && reviews.length === 0) {
		return (
			<div className={css(styleSheet.container)}>
				{header}
				{filtersSection}
				<div className={css(styleSheet.loadingContainer)}>
					<LoadingSpinner />
				</div>
			</div>
		);
	}

	if (reviews.length === 0) {
		return (
			<div className={css(styleSheet.container)}>
				{header}
				{filtersSection}
				<div className={css(styleSheet.emptyState)}>
					{hasActiveFilters ? 'No reviews match your filters' : 'No reviews yet'}
				</div>
			</div>
		);
	}

	return (
		<div className={css(styleSheet.container)}>
			{header}
			{filtersSection}
			<div className={css(styleSheet.list)}>
				{reviews.map((review, idx) => (
					<ReviewCard key={review.id || idx} review={review} onClick={() => handleReviewClick(review)} />
				))}
			</div>
			{hasNextPage && !isFetchingNextPage && <Waypoint bottomOffset='-200px' onEnter={() => fetchNextPage()} />}
			{isFetchingNextPage && (
				<div className={css(styleSheet.loadingMore)}>
					<LoadingSpinner type='small' />
				</div>
			)}

			<TestimonialModal
				accountId={accountId}
				impersonationContext={impersonationContext}
				onClose={handleCloseModal}
				selectedReview={selectedReview}
			/>
		</div>
	);
};
